import React, { useState, useEffect } from "react";
import { ArrowCircleRight } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Typography,
  IconButton,
  InputAdornment,
  Box,
  Button,
  FormControlLabel,
  Checkbox,
  Stack,
  Link,
} from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  setToken,
  getStoredToken,
  getRememberMeToken,
  setRememberMeToken,
  removeRememberMeToken,
  removeToken,
  parseJwt,
  isTokenExpired,
} from "../../utils/auth";
import { signIn } from "../../utils/loginApi";
import LoginTextField from "./LoginTextField";
import OtpInput from "./OtpInput";

const PARTIAL_CONTENT_CODE = 206;
const NUMBER_OF_OTP_DIGITS = 6;

const dangerStyle = { color: "#ff8a80", mt: 1 };

const successBannerStyle = {
  color: "#ffffff",
  textAlign: "center",
  fontSize: "14px",
  mb: 2,
  p: 1.5,
  backgroundColor: "#2e7d32",
  borderRadius: 1,
};

export default function LoginScreen() {
  const { t, i18n } = useTranslation("common", { keyPrefix: "login" });
  const { register, handleSubmit, setValue } = useForm();
  const [searchParams] = useSearchParams();
  const [dbErrors, setDbErrors] = useState(null);
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [rememberMeTokenRemoved, setRememberMeTokenRemoved] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const isResetSuccess = searchParams.get("reset_success") === "true";

  // Drop a stale submit error the moment the user edits any field.
  const clearError = () => setDbErrors((prev) => (prev ? null : prev));

  const onChange = (value) => {
    setOtpCode(value);
    clearError();
  };

  const handleRedirectLocation = (payload) => {
    try {
      const userType = payload.type;
      let location = searchParams.get("redirect");

      // Only a same-origin relative path may be replayed after login. "//host"
      // and "/\host" are protocol-relative escapes, not paths.
      if (location && !/^\/(?![/\\])/.test(location)) {
        location = null;
      }

      // A v2 redirect target is handed through untouched; the v2 layout resolves
      // its own scope.
      if (location && location.includes("/v2/")) return location;

      // Drop a redirect the current role can't reach.
      if (
        (userType === "guardian" && !location?.includes("guardian")) ||
        (userType === "student" && !location?.includes("student")) ||
        (userType === "staff" && !location?.includes("school"))
      ) {
        location = null;
      }

      // Default landing per role.
      if (!location) {
        if (userType === "staff" || userType === "administrator") {
          location = `/v2/school/${payload.default_school.slug}`;
        } else if (userType === "guardian") {
          location = "/guardian";
        } else if (userType === "student") {
          location = "/student";
        }
      }

      return location;
    } catch (e) {
      return null;
    }
  };

  const onSubmit = async (data) => {
    setDbErrors(null);
    try {
      const rememberMeToken = rememberMeTokenRemoved
        ? null
        : getRememberMeToken();

      const {
        status,
        data: payload,
        authorization,
      } = await signIn({
        email: data.email,
        password: data.password,
        otpCode: data.otpCode,
        rememberMe: data.remember_me,
        rememberMeToken,
        locale: i18n.language,
      });

      if (status === PARTIAL_CONTENT_CODE) {
        if (getRememberMeToken()) {
          removeRememberMeToken();
          setRememberMeTokenRemoved(true);
        }
        setShowOtpScreen(true);
        return;
      }

      if (payload.message === "You are logged in.") {
        setToken(authorization);
        if (payload.remember_me_token) {
          setRememberMeToken(payload.remember_me_token);
        }
        // Never replace() into null: a malformed payload must still land the
        // user somewhere reachable.
        window.location.replace(
          handleRedirectLocation(payload) ||
            (payload?.default_school?.slug
              ? `/v2/school/${payload.default_school.slug}`
              : "/")
        );
        return;
      }

      setDbErrors(payload?.message || payload?.error || t("genericError"));
    } catch (e) {
      setDbErrors(t("genericError"));
    }
  };

  const handleLoggedInUser = () => {
    const token = getStoredToken();
    if (!token) return;

    // A dead token is no proof of a live session. Drop it and let the form
    // render instead of bouncing to a page the server will reject.
    if (isTokenExpired(token)) {
      removeToken();
      return;
    }

    try {
      const decoded = parseJwt(token);
      if (!decoded?.user) return;

      if (decoded.staff && decoded.default_school_id) {
        // Honor the school the staff last viewed (stored as a raw slug by the
        // school layout) so returning to the app root does not bounce a
        // multi-school user back to their default school. Fall back to the JWT
        // default when nothing was stored yet.
        let lastSchoolSlug = null;
        try {
          lastSchoolSlug = window.localStorage.getItem("current_school_slug");
        } catch (storageError) {
          lastSchoolSlug = null;
        }
        // Serialization accidents ("null", "undefined", "") must not become a
        // school slug; a stored slug that no longer resolves ends on the 404
        // page, whose home button drops it.
        if (
          !lastSchoolSlug ||
          lastSchoolSlug === "null" ||
          lastSchoolSlug === "undefined"
        ) {
          lastSchoolSlug = null;
        }
        const targetSlug = lastSchoolSlug || decoded.default_school_id;
        window.location.replace(`/v2/school/${encodeURIComponent(targetSlug)}`);
      } else if (decoded.guardian?.id) {
        window.location.replace("/guardian");
      } else if (decoded.student?.id) {
        window.location.replace("/student");
      }
    } catch (e) {
      removeToken();
    }
  };

  useEffect(() => {
    if (searchParams.get("force_logout") === "true") {
      removeToken();
      return;
    }
    // Landing here with a ?redirect= means the server (or an API 401) just
    // rejected this session as unauthenticated. A stale token left in storage
    // must not auto-bounce us back to that protected page, or login and the
    // page ping-pong forever. Drop it and show the form; after sign-in,
    // handleRedirectLocation honors the redirect target.
    if (searchParams.get("redirect")) {
      removeToken();
      return;
    }
    handleLoggedInUser();
  }, []);

  useEffect(() => {
    if (otpCode.length === NUMBER_OF_OTP_DIGITS) {
      setValue("otpCode", otpCode);
    }
  }, [otpCode]);

  if (showOtpScreen) {
    return (
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        <Typography align="center" sx={{ mb: 2 }}>
          {t("enterMfaCode")}
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <OtpInput otpCode={otpCode} onChange={onChange} />
          <Stack sx={{ mt: 4, alignItems: "center", gap: 3 }}>
            <FormControlLabel
              sx={{ m: 0, alignItems: "center" }}
              control={
                <Checkbox
                  sx={{
                    flexShrink: 0,
                    color: "white",
                    "&.Mui-checked": { color: "white" },
                  }}
                  {...register("remember_me", {})}
                />
              }
              label={
                <Typography sx={{ lineHeight: 1.25 }}>
                  {t("notRequireCodeFor", { days: 30 })}
                </Typography>
              }
            />
            <Button
              type="submit"
              variant="contained"
              sx={{
                px: 4,
                color: "white",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {t("continue")}
            </Button>
            {dbErrors && <Typography sx={dangerStyle}>{dbErrors}</Typography>}
          </Stack>
        </form>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 348 }}>
      {isResetSuccess && (
        <Typography sx={successBannerStyle}>
          Your password has been successfully updated. Please log in using your
          new password.
        </Typography>
      )}
      <Typography align="center" sx={{ mb: 2 }}>
        {t("signInHeader")}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <LoginTextField
          required
          autoFocus
          type="email"
          id="email-required"
          label={t("enterEmail")}
          variant="filled"
          size="small"
          margin="dense"
          fullWidth
          {...register("email", { required: true, onChange: clearError })}
          slotProps={{
            htmlInput: {
              inputMode: "email",
              autoCapitalize: "none",
              autoCorrect: "off",
              spellCheck: false,
            },
          }}
        />

        <LoginTextField
          required
          type="password"
          id="password-required"
          label={t("enterPassword")}
          size="small"
          variant="filled"
          margin="dense"
          fullWidth
          {...register("password", { required: true, onChange: clearError })}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    type="submit"
                    aria-label={t("signInHeader")}
                    sx={{ p: 0, "&:hover": { background: "transparent" } }}
                  >
                    <ArrowCircleRight
                      color="success"
                      sx={{ height: 50, width: 40 }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        {dbErrors && <Typography sx={dangerStyle}>{dbErrors}</Typography>}
      </form>

      <Box sx={{ mt: { xs: 5, md: 8 }, textAlign: "center" }}>
        <Link
          component={RouterLink}
          underline="hover"
          sx={{ color: "#ffffff", fontSize: 18 }}
          to={`/forgot-password?locale=${i18n.language}`}
        >
          {t("forgotPassword")}
        </Link>
      </Box>
    </Box>
  );
}
